import React, { useContext, useEffect, useRef, useState } from 'react';
import { EllipsisHorizontalIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon, ChevronDownIcon, ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useWarehouseProduct } from '../../../hooks/useWarehouseProduct';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import { UserContext } from '../../../context/userContext';
import { WarehouseProduct } from '../../../models/WarehouseProduct';
import { Text } from '../../../components/Text';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { WarehouseProductService } from '../../../services/warehouseProductService';
import ExcelJS from 'exceljs';
import dateFormat from 'dateformat';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import showSuccessToast from '../../../components/Toasts/ShowSuccessToast';

const COLUMNS = [
    { key: 'description', label: 'Description' },
    { key: 'itemNo', label: 'Item No' },
    { key: 'quantity', label: 'Current Quantity' },
    { key: 'batches', label: 'Batches' },
    { key: 'updated', label: 'Updated Time' },
];
const SORT_OPTIONS = [
    { label: 'Newest updated', value: 'newest' },
    { label: 'Oldest updated', value: 'oldest' },
];

type ImportRow = {
    description: string;
    quantity: number;
    batchNo?: string;
    expiryDate?: string;
    stockGroup?: string;
    /** When set with batch + expiry, written to batch line; otherwise row `quantity` is used. */
    batchQuantity?: number;
};

const STOCK_GROUP_HEADER_NORMS = new Set([
    'stockgroup',
    'groupcode',
    'stockcode',
    'productgroup',
    'warehouseproductgroup',
]);

const BATCH_QTY_HEADER_NORMS = new Set([
    'batchqty',
    'batchquantity',
    'qtybatch',
    'batqty',
    'utdbatchqty',
]);

const normHeader = (raw: string) => raw.toLowerCase().replace(/[\s_-]+/g, '').trim();

/** ExcelJS: quantity cells may be numbers, strings, or formula objects `{ formula, result }`. */
const excelCellToNumber = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'object' && value !== null && 'result' in value) {
        return excelCellToNumber((value as { result?: unknown }).result);
    }
    if (typeof value === 'string') {
        const n = Number(value.replace(/,/g, '').trim());
        return Number.isNaN(n) ? null : n;
    }
    return null;
};

const cellValueToYmd = (value: unknown): string | undefined => {
    if (value == null || value === '') return undefined;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    if (typeof value === 'number' && value > 1000 && value < 1200000) {
        const epoch = new Date(Date.UTC(1899, 11, 30));
        const ms = epoch.getTime() + value * 86400000;
        const dt = new Date(ms);
        const y = dt.getUTCFullYear();
        const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
        const d = String(dt.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return s || undefined;
};

/** Parse one SQL VALUES tuple into raw value strings (handles quoted strings with '' escape). */
const parseSqlTupleValues = (tuple: string): string[] => {
    const values: string[] = [];
    let i = 0;
    const t = tuple.trim();
    while (i < t.length) {
        while (i < t.length && /[\s,]/.test(t[i])) i++;
        if (i >= t.length) break;
        if (t[i] === "'") {
            i++;
            let sb = '';
            while (i < t.length) {
                if (t[i] === "'" && t[i + 1] === "'") {
                    sb += "'";
                    i += 2;
                    continue;
                }
                if (t[i] === "'") {
                    i++;
                    break;
                }
                sb += t[i];
                i++;
            }
            values.push(sb);
        } else {
            const start = i;
            while (i < t.length && t[i] !== ',') i++;
            values.push(t.slice(start, i).trim());
            if (t[i] === ',') i++;
        }
    }
    return values;
};

/** Build and download a plain-text file listing all import validation errors. */
const downloadImportErrorsTxt = (errors: string[]) => {
    try {
        const iso = new Date().toISOString();
        const datePart = iso.slice(0, 10);
        const lines = [
            `Import errors (${iso})`,
            `Total: ${errors.length}`,
            '',
            ...errors.map((msg, i) => `${i + 1}. ${msg}`),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `import-errors-${datePart}.txt`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch {
        showErrorToast('Could not download the import errors report.');
    }
};

const formatMs = (ms: number | null | undefined): string => {
    if (ms == null || Number.isNaN(ms) || ms < 0) return 'Estimating...';
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
};

const WarehouseProductPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const {
        loading,
        error,
        deleteProduct,
        products,
        fetchAllFilteredProductsOffset,
        hasMore,
        // @ts-ignore total exposed in hook
        totalProducts,
    } = useWarehouseProduct();
    const { warehouses } = useWarehouses();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const productListRef = useRef<HTMLDivElement>(null);
    const scrollPosition = useRef(0);
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState({
        startDate: '',
        endDate: '',
        year: '',
        warehouseId: ''
    });
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importRows, setImportRows] = useState<ImportRow[]>([]);
    const [importResetFirst, setImportResetFirst] = useState(true);
    const [importSkipGroupCode, setImportSkipGroupCode] = useState(false);
    const [importWarehouseId, setImportWarehouseId] = useState<string>('');
    const [isImporting, setIsImporting] = useState(false);
    const [importParseError, setImportParseError] = useState<string | null>(null);
    const [importJobStatus, setImportJobStatus] = useState<{
        phase?: string;
        processed: number;
        total: number;
        percent: number;
        elapsedMs: number;
        etaMs: number | null;
    } | null>(null);

    // Restore search from URL and fetch (on load and when URL params change)
    useEffect(() => {
        document.title = "Products";
        const q = searchParams.get('q') ?? '';
        const from = searchParams.get('from') ?? '';
        const to = searchParams.get('to') ?? '';
        const year = searchParams.get('year') ?? '';
        const warehouse = searchParams.get('warehouse') ?? '';
        const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
        setSearchQuery(q);
        setFilterStartDate(from);
        setFilterEndDate(to);
        setFilterYear(year);
        setFilterWarehouseId(warehouse);
        setCurrentPage(page);
        setActiveSearchQuery(q);
        setActiveFilters({ startDate: from, endDate: to, year, warehouseId: warehouse });
        const newOffset = (page - 1) * limit;
        setOffset(newOffset);
        fetchAllFilteredProductsOffset(limit, newOffset, q, from, to, year, warehouse);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleSearch = () => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('q', searchQuery.trim());
            if (filterStartDate) next.set('from', filterStartDate);
            else next.delete('from');
            if (filterEndDate) next.set('to', filterEndDate);
            else next.delete('to');
            if (filterYear) next.set('year', filterYear);
            else next.delete('year');
            if (filterWarehouseId) next.set('warehouse', filterWarehouseId);
            else next.delete('warehouse');
            next.set('page', '1');
            return next;
        });
    };

    const handleReset = () => {
        setSearchQuery('');
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
        setSearchParams({});
    };

    // Filter products based on active search/filters
    const filteredProducts = products.filter((product) => {
        const matchesSearch = activeSearchQuery
            ? product.description?.toLowerCase().includes(activeSearchQuery.toLowerCase())
            : true;
        const matchesStartDate = !activeFilters.startDate || new Date(product.created_time) >= new Date(activeFilters.startDate);
        const matchesEndDate = !activeFilters.endDate || new Date(product.created_time) <= new Date(activeFilters.endDate);
        const matchesYear = !activeFilters.year || new Date(product.created_time).getFullYear().toString() === activeFilters.year;
        const matchesWarehouse = !activeFilters.warehouseId || product.warehouse_id === Number(activeFilters.warehouseId);

        return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
    });

    const availableYears = Array.from(
        new Set(
            products
                .map((p) => new Date(p.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [activeFilters.startDate, activeFilters.endDate, activeFilters.year, activeFilters.warehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(activeFilters.startDate ? [{ key: 'from', label: `From: ${activeFilters.startDate}`, onClear: () => { setFilterStartDate(''); setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('from'); return next; }); } }] : []),
        ...(activeFilters.endDate ? [{ key: 'to', label: `To: ${activeFilters.endDate}`, onClear: () => { setFilterEndDate(''); setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('to'); return next; }); } }] : []),
        ...(activeFilters.year ? [{ key: 'year', label: `Year: ${activeFilters.year}`, onClear: () => { setFilterYear(''); setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('year'); return next; }); } }] : []),
        ...(activeFilters.warehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === activeFilters.warehouseId)?.warehouse_name || activeFilters.warehouseId}`,
            onClear: () => { setFilterWarehouseId(''); setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('warehouse'); return next; }); },
        }] : []),
    ];

    const handleOpenDeleteDialog = (productId: string) => {
        setSelectedProductId(productId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedProductId) return;

        setIsDeleting(true);
        try {
            await deleteProduct(Number(selectedProductId));
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete product:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const parseExcelFile = async (file: File): Promise<ImportRow[]> => {
        const buffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const sheet = workbook.worksheets[0];
        if (!sheet) return [];
        const rows: ImportRow[] = [];
        const headerRow = sheet.getRow(1);
        let descCol = -1;
        let qtyCol = -1;
        let batchCol = -1;
        let expiryCol = -1;
        let stockGroupCol = -1;
        let batchQtyCol = -1;
        headerRow.eachCell((cell, colNumber) => {
            const n = normHeader(cell.value?.toString() ?? '');
            if (n === 'description' || n === 'descr') descCol = colNumber;
            if (n === 'quantity' || n === 'qty' || n === 'utdqty') qtyCol = colNumber;
            if (n === 'batch' || n === 'batchno' || n === 'batchnumber') batchCol = colNumber;
            if (
                n === 'expirydate' ||
                n === 'expdate' ||
                n === 'expiry' ||
                n === 'expirationdate' ||
                n === 'expiration' ||
                n === 'bestbefore'
            )
                expiryCol = colNumber;
            if (STOCK_GROUP_HEADER_NORMS.has(n)) stockGroupCol = colNumber;
            if (BATCH_QTY_HEADER_NORMS.has(n)) batchQtyCol = colNumber;
        });
        if (descCol < 0 || qtyCol < 0) {
            throw new Error(
                'Excel must have description (or descr) and quantity (qty / utd qty). Optional: stock group, batch, expiry date, batch quantity (batch qty / batch quantity).'
            );
        }
        for (let i = 2; i <= sheet.rowCount; i++) {
            const row = sheet.getRow(i);
            const desc = row.getCell(descCol).value != null ? String(row.getCell(descCol).value).trim() : '';
            const qtyParsed = excelCellToNumber(row.getCell(qtyCol).value);
            if (desc === '') continue;
            if (qtyParsed == null || qtyParsed < 0) continue;
            const out: ImportRow = { description: desc, quantity: qtyParsed };
            if (stockGroupCol >= 0) {
                const g = row.getCell(stockGroupCol).value;
                const gs = g != null ? String(g).trim() : '';
                if (gs !== '') out.stockGroup = gs;
            }
            if (batchCol >= 0) {
                const b = row.getCell(batchCol).value;
                const bs = b != null ? String(b).trim() : '';
                if (bs !== '') out.batchNo = bs;
            }
            if (expiryCol >= 0) {
                const e = cellValueToYmd(row.getCell(expiryCol).value);
                if (e) out.expiryDate = e;
            }
            if (batchQtyCol >= 0) {
                const bq = excelCellToNumber(row.getCell(batchQtyCol).value);
                if (bq != null && bq >= 0) out.batchQuantity = bq;
            }
            rows.push(out);
        }
        return rows;
    };

    const parseSqlFile = (text: string): ImportRow[] => {
        const rows: ImportRow[] = [];
        const colMatch = text.match(
            /INSERT\s+INTO\s+[`'"]?[\w.]+[`'"]?\s*\(\s*([^)]+)\s*\)\s*VALUES/i
        );
        let colNorms: string[] | null = null;
        if (colMatch) {
            colNorms = colMatch[1].split(',').map((c) => normHeader(c.trim().replace(/^`|`$/g, '')));
        }
        const valuesMatch = text.match(/VALUES\s*\(([\s\S]*)\)\s*;?/i);
        if (!valuesMatch) {
            throw new Error('SQL file must contain INSERT INTO ... VALUES (...), (...); pattern.');
        }
        const valuesBlock = valuesMatch[1];
        const tuples = valuesBlock.split(/\),\s*\(/).map((s) => s.replace(/^\s*\(?|\)?\s*$/g, '').trim());

        const idxDesc =
            colNorms == null
                ? -1
                : colNorms.findIndex((n) => n === 'description' || n === 'descr');
        const idxQty =
            colNorms == null ? -1 : colNorms.findIndex((n) => n === 'quantity' || n === 'qty' || n === 'utdqty');
        const idxBatch =
            colNorms == null ? -1 : colNorms.findIndex((n) => n === 'batch' || n === 'batchno' || n === 'batchnumber');
        const idxExpiry =
            colNorms == null
                ? -1
                : colNorms.findIndex((n) =>
                      [
                          'expirydate',
                          'expdate',
                          'expiry',
                          'expirationdate',
                          'expiration',
                          'bestbefore',
                      ].includes(n)
                  );
        const idxStockGroup =
            colNorms == null ? -1 : colNorms.findIndex((n) => STOCK_GROUP_HEADER_NORMS.has(n));
        const idxBatchQty =
            colNorms == null ? -1 : colNorms.findIndex((n) => BATCH_QTY_HEADER_NORMS.has(n));

        const useNamed = colNorms != null && idxDesc >= 0 && idxQty >= 0;
        if (colNorms != null && !useNamed) {
            throw new Error(
                'SQL INSERT must list columns including description (or descr) and quantity, qty, or UTD Qty.'
            );
        }

        for (const tuple of tuples) {
            if (useNamed) {
                const vals = parseSqlTupleValues(tuple);
                if (vals.length <= Math.max(idxDesc, idxQty)) continue;
                const desc = vals[idxDesc].replace(/''/g, "'").trim();
                const qtyStr = vals[idxQty].replace(/^'|'$/g, '').replace(/,/g, '').trim();
                const qty = Number(qtyStr);
                if (desc === '' || Number.isNaN(qty) || qty < 0) continue;
                const out: ImportRow = { description: desc, quantity: qty };
                if (idxStockGroup >= 0 && vals[idxStockGroup] != null) {
                    const g = vals[idxStockGroup].replace(/''/g, "'").trim().replace(/^'|'$/g, '');
                    if (g !== '' && g.toUpperCase() !== 'NULL') out.stockGroup = g;
                }
                if (idxBatch >= 0 && vals[idxBatch] != null) {
                    const b = vals[idxBatch].replace(/''/g, "'").trim();
                    if (b !== '' && b.toUpperCase() !== 'NULL') out.batchNo = b.replace(/^'|'$/g, '');
                }
                if (idxExpiry >= 0 && vals[idxExpiry] != null) {
                    const e = vals[idxExpiry].replace(/''/g, "'").trim().replace(/^'|'$/g, '');
                    if (e !== '' && e.toUpperCase() !== 'NULL') out.expiryDate = e;
                }
                if (idxBatchQty >= 0 && vals[idxBatchQty] != null) {
                    const bqs = vals[idxBatchQty].replace(/''/g, "'").trim().replace(/^'|'$/g, '');
                    const bqn = Number(bqs.replace(/,/g, ''));
                    if (bqs !== '' && bqs.toUpperCase() !== 'NULL' && !Number.isNaN(bqn) && bqn >= 0) {
                        out.batchQuantity = bqn;
                    }
                }
                rows.push(out);
                continue;
            }

            const firstQuote = tuple.indexOf("'");
            if (firstQuote < 0) continue;
            let endQuote = firstQuote + 1;
            while (endQuote < tuple.length) {
                const next = tuple.indexOf("'", endQuote);
                if (next < 0) break;
                if (tuple[next + 1] === "'") {
                    endQuote = next + 2;
                    continue;
                }
                endQuote = next;
                break;
            }
            const descRaw = tuple.substring(firstQuote + 1, endQuote);
            const desc = descRaw.replace(/''/g, "'").trim();
            const rest = tuple.substring(endQuote + 1).trim();
            const numMatch = rest.match(/,\s*([\d.,]+)/);
            const qty = numMatch ? Number(String(numMatch[1]).replace(/,/g, '')) : NaN;
            if (desc === '' || Number.isNaN(qty) || qty < 0) continue;
            rows.push({ description: desc, quantity: qty });
        }

        return rows;
    };

    const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        setImportFile(file ?? null);
        setImportRows([]);
        setImportParseError(null);
        if (!file) return;
        const ext = (file.name || '').toLowerCase();
        try {
            if (ext.endsWith('.xlsx') || ext.endsWith('.xls')) {
                const rows = await parseExcelFile(file);
                setImportRows(rows);
            } else if (ext.endsWith('.sql')) {
                const text = await file.text();
                const rows = parseSqlFile(text);
                setImportRows(rows);
            } else {
                setImportParseError('Please choose an Excel (.xlsx, .xls) or SQL (.sql) file.');
            }
        } catch (err) {
            setImportParseError(err instanceof Error ? err.message : 'Failed to parse file.');
        }
    };

    const handleImportSubmit = async () => {
        if (importRows.length === 0) {
            showErrorToast('No rows to import. Choose a valid file first.');
            return;
        }
        setIsImporting(true);
        setImportJobStatus({
            phase: 'queued',
            processed: 0,
            total: Math.max(importRows.length * 2, 1),
            percent: 0,
            elapsedMs: 0,
            etaMs: null,
        });
        try {
            const options: { warehouseId?: number; resetFirst?: boolean; skipGroupCode?: boolean } = {
                resetFirst: importResetFirst,
                skipGroupCode: importSkipGroupCode,
            };
            if (importWarehouseId) options.warehouseId = Number(importWarehouseId);
            const started = await WarehouseProductService.startImportQuantities(importRows, options);
            const jobId = started.jobId;
            let polling = true;
            let result: { updated: number; inserted: number; batchesUpserted?: number; errors: string[] } | null = null;
            let pollErrors = 0;
            while (polling) {
                let status;
                try {
                    status = await WarehouseProductService.getImportQuantitiesStatus(jobId);
                    pollErrors = 0;
                } catch {
                    pollErrors += 1;
                    if (pollErrors >= 4) {
                        throw new Error('Import status polling interrupted.');
                    }
                    await new Promise((resolve) => setTimeout(resolve, 1500 * pollErrors));
                    continue;
                }
                setImportJobStatus({
                    phase: status.phase,
                    processed: status.processed ?? 0,
                    total: status.total ?? 1,
                    percent: status.percent ?? 0,
                    elapsedMs: status.elapsedMs ?? 0,
                    etaMs: status.etaMs ?? null,
                });
                if (status.status === 'completed') {
                    result = status.result ?? { updated: 0, inserted: 0, batchesUpserted: 0, errors: [] };
                    polling = false;
                    break;
                }
                if (status.status === 'failed') {
                    throw new Error((status.errors && status.errors[0]) || 'Import failed.');
                }
                await new Promise((resolve) => setTimeout(resolve, 1200));
            }
            if (!result) throw new Error('Import status polling interrupted.');
            const batchPart =
                result?.batchesUpserted != null && result.batchesUpserted > 0
                    ? `, ${result.batchesUpserted} batch row(s) written`
                    : '';
            showSuccessToast(
                `Import complete: ${result?.updated ?? 0} updated, ${result?.inserted ?? 0} inserted${batchPart}.`
            );
            if (result?.errors?.length) {
                result.errors.slice(0, 3).forEach((msg) => showErrorToast(msg));
                downloadImportErrorsTxt(result.errors);
            }
            setIsImportOpen(false);
            setImportFile(null);
            setImportRows([]);
            setImportParseError(null);
            fetchAllFilteredProductsOffset(limit, offset, activeSearchQuery, '', '', activeFilters.year, activeFilters.warehouseId);
        } catch (err: unknown) {
            const message = err && typeof err === 'object' && 'response' in err && err.response && typeof (err.response as { data?: { message?: string } }).data?.message === 'string'
                ? (err.response as { data: { message: string } }).data.message
                : 'Import failed.';
            showErrorToast(message);
        } finally {
            setIsImporting(false);
            setImportJobStatus(null);
        }
    };

    const handleImportDialogClose = () => {
        if (!isImporting) {
            setIsImportOpen(false);
            setImportFile(null);
            setImportRows([]);
            setImportParseError(null);
            setImportJobStatus(null);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Products</Heading>
                </div>
                <div className="flex items-center gap-2">
                    <Dropdown>
                        <DropdownButton outline>
                            <EllipsisHorizontalIcon />
                            More
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                            <DropdownItem onClick={() => setIsImportOpen(true)}>
                                <ArrowUpTrayIcon />
                                <DropdownLabel>Import</DropdownLabel>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                    <Button onClick={() => navigate('/gp/inventories/products/add')}>
                        <PlusIcon />
                        Create new product
                    </Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={
                <ListToolbarSearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    placeholder="Search product…"
                />
            }>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={handleReset} />

            {/* Product Table */}
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'description') && <TableHeader>Description</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'itemNo') && <TableHeader>Item No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'quantity') && <TableHeader>Current Quantity</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'batches') && <TableHeader>Batches</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'updated') && <TableHeader>Updated Time</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(products.length > 0) ? [...products].sort((a, b) => {
                                    const diff = new Date(a.updated_time || 0).getTime() - new Date(b.updated_time || 0).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((product, index) => (
                                    <TableRow key={product.id} title={`${product.barcode}`}>
                                        <TableCell>{(currentPage - 1) * limit + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'description') && <TableCell className="text-zinc-500">{product.description ?? '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'itemNo') && <TableCell className="text-zinc-500">{product.item_no ?? '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'quantity') && <TableCell className="text-zinc-500">{product.current_quantity ?? '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'batches') && <TableCell className="text-zinc-500">{product.batch_count ?? 0}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'updated') && <TableCell className="text-zinc-500">
                                            {product.updated_time ? dateFormat(product.updated_time, 'mmm dd, yyyy HH:MM') : '-'}
                                        </TableCell>}
                                        <TableCell className="py-5 grid grid-cols-3 text-zinc-500">
                                            <a onClick={() => navigate(`/gp/inventories/products/edit/${product.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/inventories/products/view/${product.id}`, { state: { returnTo: location.pathname + location.search } })} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5'>
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(product.id.toString())}>Delete</DropdownItem>
                                                    <DropdownItem onClick={() => navigate(`/gp/inventories/products/prices/add/${product.id}`)}>Add Product Price</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">No product found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil((totalProducts || filteredProducts.length) / limit)}
                            onPageChange={(page) => setSearchParams((prev) => {
                                    const next = new URLSearchParams(prev);
                                    next.set('page', String(page));
                                    return next;
                                })}
                            totalResult={totalProducts || filteredProducts.length}
                            dataName={'Products'}
                            pageSize={limit}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog
                open={isFilterOpen}
                onClose={() => { handleSearch(); setIsFilterOpen(false); }}
                onReset={handleReset}
            >
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year (Inserted Year)</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.filter((warehouse) => warehouse.hq !== '1').map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete product</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this product? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Import Quantities Dialog */}
            <Dialog open={isImportOpen} onClose={handleImportDialogClose}>
                <DialogTitle>Import quantities</DialogTitle>
                <DialogDescription>
                    Choose an Excel or SQL file with description and quantity (qty / UTD Qty). Optional: stock group (matches product group code or name, case-insensitive). If any row includes a stock group, every row must include one; the same description under different groups is treated as different products unless you tick skip stock group matching below. Optional columns: batch, expiry date, batch quantity (batch qty / batch quantity — used for the batch line when batch and expiry are set; otherwise the row quantity is used). Product total quantity is still the sum of each row&apos;s main quantity column for the same description and group. Formula cells in Excel are read from their calculated result. Existing batch lines for those products are removed and replaced from the file.
                </DialogDescription>
                <DialogBody>
                    <div className="space-y-4">
                        <div>
                            <Text>File</Text>
                            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-zinc-50">
                                <ArrowUpTrayIcon className="h-5 w-5 text-zinc-500" />
                                <span className="text-zinc-700">{importFile ? importFile.name : 'Choose Excel or SQL file'}</span>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls,.sql"
                                    className="sr-only"
                                    onChange={handleImportFileChange}
                                />
                            </label>
                            {importParseError && <p className="mt-1 text-sm text-red-600">{importParseError}</p>}
                            {importRows.length > 0 && !importParseError && (
                                <p className="mt-1 text-sm text-zinc-600">{importRows.length} rows found</p>
                            )}
                            {isImporting && importJobStatus && (
                                <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-2 text-sm text-zinc-700">
                                    <p>
                                        {importJobStatus.phase || 'processing'} - {importJobStatus.percent}% ({importJobStatus.processed}/{importJobStatus.total})
                                    </p>
                                    <p className="text-zinc-500">
                                        Elapsed: {formatMs(importJobStatus.elapsedMs)} | ETA: {formatMs(importJobStatus.etaMs)}
                                    </p>
                                </div>
                            )}
                        </div>
                        {(currentUser?.role?.toString() === '1') && (
                            <div>
                                <Text>Apply to warehouse</Text>
                                <Dropdown>
                                    <DropdownButton color="white" className="w-full justify-between">
                                        {importWarehouseId ? warehouses?.find((w) => w.id?.toString() === importWarehouseId)?.warehouse_name : 'All warehouses'}
                                        <ChevronDownIcon className="h-4 w-4" />
                                    </DropdownButton>
                                    <DropdownMenu>
                                        <DropdownItem onClick={() => setImportWarehouseId('')}>All warehouses</DropdownItem>
                                        {warehouses
                                            ?.filter((w) => w.hq !== '1')
                                            .map((w) => (
                                                <DropdownItem key={w.id} onClick={() => setImportWarehouseId(w.id?.toString() ?? '')}>
                                                    {w.warehouse_name}
                                                </DropdownItem>
                                            ))}
                                    </DropdownMenu>
                                </Dropdown>
                            </div>
                        )}
                        <label className="flex cursor-pointer items-center gap-2">
                            <input
                                type="checkbox"
                                checked={importResetFirst}
                                onChange={(e) => setImportResetFirst(e.target.checked)}
                                className="h-4 w-4 rounded border-zinc-300"
                            />
                            <Text>Reset all quantities to 0 before import</Text>
                        </label>
                        <label className="flex cursor-pointer items-center gap-2">
                            <input
                                type="checkbox"
                                checked={importSkipGroupCode}
                                onChange={(e) => setImportSkipGroupCode(e.target.checked)}
                                className="h-4 w-4 rounded border-zinc-300"
                            />
                            <Text>Skip stock group matching (merge by description only)</Text>
                        </label>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={handleImportDialogClose} disabled={isImporting}>
                        Cancel
                    </Button>
                    <Button onClick={handleImportSubmit} disabled={isImporting || importRows.length === 0}>
                        {isImporting ? <Loading /> : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WarehouseProductPage;
